// Storage Inventory Types

// Asset category identifier used for tab selection and data routing
export type AssetCategory = 'servers' | 'elementals' | 'network_devices';

// Operational status values for server and network device assets.
// The known values below drive default colors/badges and editor autocomplete,
// but statuses are user-configurable in Settings, so any custom string is also
// allowed (the `string & {}` keeps autocomplete for the known values).
export type AssetStatus =
  | 'in storage'
  | 'in production'
  | 'unknown'
  | 'has issues'
  | 'decommissioned'
  | 'Missing/Unknown Can Not Find During Audit (Infradev-1803)'
  | (string & NonNullable<unknown>);

// Canonical default status list. Used to seed the configurable `statuses` setting
// so every status dropdown (search filter, add/edit form, bulk add, default status)
// draws from one source. Editing statuses in Settings overrides this list.
export const DEFAULT_ASSET_STATUSES: string[] = [
  'in storage',
  'in production',
  'unknown',
  'has issues',
  'decommissioned',
  'Missing/Unknown Can Not Find During Audit (Infradev-1803)',
];

// The status auto-applied when an asset is placed at the "Unknown Site" during the
// Infradev-1803 audit. Kept as a shared constant so the form and any other caller
// reference the exact same string.
export const MISSING_STATUS =
  'Missing/Unknown Can Not Find During Audit (Infradev-1803)';

// Prefix used to recognize the audit "Unknown Site" entry regardless of the rest
// of its (editable) label. Matched case-insensitively.
export const UNKNOWN_SITE_PREFIX = 'unknown site';

// True when a site label refers to the audit "Unknown Site" bucket.
export function isUnknownSite(site: unknown): boolean {
  return typeof site === 'string' && site.trim().toLowerCase().startsWith(UNKNOWN_SITE_PREFIX);
}

// Warranty status derived from support_end_date comparison
export type WarrantyStatus = 'expired' | 'expiring-soon' | 'valid' | 'unknown';

// Common fields shared across all asset types
interface BaseAssetFields {
  id: string;                    // UUID v4
  last_known_contact?: string;   // Last known contact for this device
  purchase_order?: string;       // PO number for financial tracking
  cost?: number;                 // Purchase cost in USD
  location_detail?: string;      // Specific location: Room > Shelf > Position
  images?: string[];             // Array of photo storage keys (photos/{category}/{uuid}.{ext}); resolved to a URL for display
}

// Server/Encoder/Appliance asset (Dell servers, KiPros, etc.)
export interface ServerAsset extends BaseAssetFields {
  site: string;                  // required
  hostname?: string;             // optional, defaults to "no_hostname"
  make: string;                  // required
  model: string;                 // required
  service_tag: string;           // required
  express_service_code?: string; // Dell Express Service Code (7-digit numeric)
  status: AssetStatus;           // required
  ship_date?: string;            // YYYY-MM-DD
  support_end_date?: string;     // YYYY-MM-DD
  cpu_model?: string;
  cpu_quantity?: number;
  ram_model?: string;
  ram_quantity?: number;
  disk_model?: string;
  disk_quantity?: number;
  embedded_nics?: string;
  integrated_nics?: string;
  addon_cards?: string;
  notes?: string;
}

// Spare Elemental encoder appliance asset
export interface ElementalAsset extends BaseAssetFields {
  site: string;                      // required
  series: string;                    // required
  manufacturer: string;              // required
  model: string;                     // required
  service_tag: string;               // required
  express_service_code?: string;     // Dell Express Service Code (7-digit numeric)
  boxed_status: 'boxed' | 'unboxed'; // required
  condition: 'new' | 'used' | 'refurbished'; // required
  status: AssetStatus;               // required
  to_be_decommissioned: boolean;     // required
  location?: string;
  notes?: string;
}

// Network device asset (switches, optics, PSUs, fans)
export interface NetworkDeviceAsset extends BaseAssetFields {
  site: string;                  // required
  make: string;                  // required
  model: string;                 // required
  serial_num: string;            // required
  status: AssetStatus;           // required
  quantity: number;              // required, 1-9999
  hostname?: string;
  support_end_date?: string;     // YYYY-MM-DD
  customer_order?: string;
  shipping_box?: string;
  tracking_num?: string;
  notes?: string;
}

// Union type for all asset categories
export type Asset = ServerAsset | ElementalAsset | NetworkDeviceAsset;

// Validation result returned by form validation functions
export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>; // field name -> error message
}

// Required fields per category for form validation
export const SERVER_REQUIRED_FIELDS: (keyof ServerAsset)[] = [
  'site', 'make', 'model', 'service_tag', 'status',
];

export const ELEMENTAL_REQUIRED_FIELDS: (keyof ElementalAsset)[] = [
  'site', 'series', 'manufacturer', 'model', 'service_tag',
  'boxed_status', 'condition', 'status', 'to_be_decommissioned',
];

export const NETWORK_DEVICE_REQUIRED_FIELDS: (keyof NetworkDeviceAsset)[] = [
  'site', 'make', 'model', 'serial_num', 'status', 'quantity',
];

// Mapping of required fields by category
export const REQUIRED_FIELDS_BY_CATEGORY: Record<AssetCategory, string[]> = {
  servers: SERVER_REQUIRED_FIELDS as string[],
  elementals: ELEMENTAL_REQUIRED_FIELDS as string[],
  network_devices: NETWORK_DEVICE_REQUIRED_FIELDS as string[],
};

// Status badge color mapping for visual differentiation
export const STATUS_COLORS: Record<AssetStatus, string> = {
  'in storage': 'blue',
  'in production': 'green',
  'unknown': 'grey',
  'has issues': 'red',
  'decommissioned': 'grey',
  'Missing/Unknown Can Not Find During Audit (Infradev-1803)': 'red',
};

// Configurable page size options for table pagination
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 500] as const;

// Default page size
export const DEFAULT_PAGE_SIZE = 100;
